"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Car, Shield, Users, CheckCircle, Phone, Star, Clock, ShieldCheck } from "lucide-react";

interface TeklifAlClientProps {
    phone: string;
}

export default function TeklifAlClient({ phone }: TeklifAlClientProps) {
    const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

    const products = [
        {
            id: "trafik",
            title: "Trafik Sigortası",
            description: "Zorunlu trafik sigortası ile yasal güvence altına girin",
            icon: Car,
            color: "blue",
            features: ["Yasal zorunluluk", "5 dakikada poliçe", "Anında teslim", "En uygun fiyat garantisi"],
            link: "/teklif-al/trafik-sigortasi",
        },
        {
            id: "kasko",
            title: "Kasko Sigortası",
            description: "Aracınızı kapsamlı risklere karşı güvence altına alın",
            icon: Shield,
            color: "green",
            features: ["Çarpma, hırsızlık, yangın", "7/24 yol yardım", "Cam kırılması", "Doğal afet teminatı"],
            link: "/teklif-al/kasko",
        },
        {
            id: "tss",
            title: "Tamamlayıcı Sağlık Sigortası",
            description: "Sağlık harcamalarınızı güvence altına alın",
            icon: ShieldCheck,
            color: "blue",
            features: ["Yatarak ve ayakta tedavi", "Özel hastane avantajı", "Check-up hizmeti", "Diş ve göz teminatı"],
            link: "/teklif-al/tamamlayici-saglik-sigortasi",
        }
    ];

    const stats = [
        { number: "50.000+", label: "Mutlu Müşteri", icon: Users },
        { number: "24/7", label: "Müşteri Desteği", icon: Clock },
        { number: "5 dk", label: "Ortalama Süre", icon: CheckCircle },
        { number: "%99", label: "Memnuniyet Oranı", icon: Star }
    ];

    const features = [
        {
            icon: ShieldCheck,
            title: "Güvenli İşlem",
            description: "256-bit SSL şifreleme ile güvenli alışveriş"
        },
        {
            icon: CheckCircle,
            title: "Anında Poliçe",
            description: "Ödemenizden hemen sonra poliçeniz e-posta adresinize gelsin"
        },
        {
            icon: Users,
            title: "Uzman Destek",
            description: "7/24 müşteri hizmetleri ile yanınızdayız"
        }
    ];

    const getColorClasses = (color: string) => {
        const colors: any = {
            blue: {
                bg: "bg-blue-50",
                border: "border-blue-200",
                text: "text-blue-600",
                button: "bg-blue-600 hover:bg-blue-700",
                light: "bg-blue-500/10"
            },
            green: {
                bg: "bg-green-50",
                border: "border-green-200",
                text: "text-green-600",
                button: "bg-green-600 hover:bg-green-700",
                light: "bg-green-500/10"
            }
        };
        return colors[color] || colors.blue;
    };

    return (
        <div className="bg-white">
            {/* Hero Section */}
            <section className="relative min-h-[80vh] flex items-center justify-center bg-linear-to-br from-blue-50 via-white to-blue-100 overflow-hidden pt-20 md:pt-24 pb-12 md:pb-16 px-4 sm:px-6">
                {/* Dekoratif arkaplan elementleri (Acente Rengine Duyarlı) */}
                <div className="absolute top-0 left-0 w-full h-1/2 bg-linear-to-b from-blue-100/50 to-transparent"></div>
                <div className="absolute top-10 md:top-20 left-4 md:left-10 w-48 md:w-72 h-48 md:h-72 rounded-full blur-2xl md:blur-3xl opacity-20" style={{ backgroundColor: 'var(--color-primary)' }}></div>
                <div className="absolute bottom-10 md:bottom-20 right-4 md:right-10 w-64 md:w-96 h-64 md:h-96 rounded-full blur-2xl md:blur-3xl opacity-15" style={{ backgroundColor: 'var(--color-primary)' }}></div>

                {/* Grid nokta deseni */}
                <div className="absolute inset-0 bg-[radial-gradient(#d1e0f8_1px,transparent_1px)] [bg-size:16px_16px] md:[bg-size:24px_24px]"></div>

                <div className="container relative z-10 mx-auto max-w-350">
                    <div className="text-center mb-16 md:mb-20">
                        <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6" style={{ color: 'var(--color-primary)', backgroundColor: 'color-mix(in srgb, var(--color-primary), transparent 90%)' }}>
                            <CheckCircle className="w-4 h-4 mr-2" /> Hızlı ve Güvenli Sigorta
                        </div>

                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
                            Sigorta <span style={{ color: 'var(--color-primary)' }}>Teklifi Alın</span>
                        </h1>

                        <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                            Size en uygun sigorta ürününü seçin, birkaç dakika içinde teklifinizi alın.
                            <span className="font-semibold" style={{ color: 'var(--color-primary)' }}> En iyi fiyat garantisi</span> ve anında poliçe teslimi.
                        </p>

                        {/* İstatistikler */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-2xl mx-auto mb-12">
                            {stats.map((stat, index) => {
                                const Icon = stat.icon;
                                return (
                                    <div key={index} className="text-center">
                                        <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 bg-white rounded-xl shadow-sm border border-gray-100">
                                            <Icon className="w-6 h-6 md:w-7 md:h-7" style={{ color: 'var(--color-primary)' }} />
                                        </div>
                                        <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">{stat.number}</div>
                                        <div className="text-gray-600 text-sm md:text-base">{stat.label}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Ürün Seçim Kartları */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12 max-w-6xl mx-auto items-stretch">
                        {products.map((product) => {
                            const Icon = product.icon;
                            const colorClasses = getColorClasses(product.color);

                            return (
                                <div
                                    key={product.id}
                                    className="relative flex" // 1. Kart sarıcısını flex yapıyoruz ki içindeki kart %100 yüksekliğe uzasın
                                    onMouseEnter={() => setHoveredProduct(product.id)}
                                    onMouseLeave={() => setHoveredProduct(null)}
                                >
                                    <div className={`flex flex-col w-full bg-white rounded-2xl md:rounded-3xl shadow-lg border-2 ${colorClasses.border} overflow-hidden transition-all duration-500 ${hoveredProduct === product.id
                                            ? 'shadow-xl scale-105 border-blue-300 z-10' // Hover anında z-index artırıldı
                                            : 'hover:shadow-xl hover:scale-[1.02] z-0'
                                        }`}>

                                        {/* Kart Header */}
                                        <div className={`p-8 ${colorClasses.bg} border-b ${colorClasses.border}`}>
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className={`w-16 h-16 rounded-2xl ${colorClasses.light} flex items-center justify-center mr-4 shrink-0`}>
                                                        <Icon className={`w-8 h-8 ${colorClasses.text}`} />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{product.title}</h3>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-gray-600 mt-1 font-medium">{product.description}</p>
                                        </div>

                                        {/* Kart Body - flex-grow eklendi ki içerik boşluğunu doldursun */}
                                        <div className="p-8 flex flex-col grow">

                                            {/* Özellikler */}
                                            <div className="space-y-4 mb-8">
                                                {product.features.map((feature, index) => (
                                                    // Metin uzarsa ikon üstte kalsın diye items-start yapıldı
                                                    <div key={index} className="flex items-start">
                                                        <div className={`w-6 h-6 rounded-full ${colorClasses.bg} flex items-center justify-center mr-3 shrink-0 mt-0.5`}>
                                                            <CheckCircle className={`w-4 h-4 ${colorClasses.text}`} />
                                                        </div>
                                                        <span className="text-gray-700 font-medium">{feature}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* CTA Butonu - mt-auto eklendi ki daima en alta yapışsın */}
                                            <Link
                                                href={product.link}
                                                className={`w-full mt-auto ${colorClasses.button} text-white py-4 px-8 rounded-xl font-semibold text-lg transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 group cursor-pointer`}
                                            >
                                                <span>Hemen Teklif Al</span>
                                                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Özellikler Section */}
            <section className="py-20 md:py-28 bg-linear-to-b from-white to-blue-50 relative px-4 sm:px-6">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16 md:mb-20">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            Neden <span style={{ color: 'var(--color-primary)' }}>Bizi Seçmelisiniz?</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Müşteri memnuniyeti odaklı hizmet anlayışımızla yanınızdayız
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-6xl mx-auto">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div key={index} className="text-center group">
                                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: 'var(--color-primary)' }}>
                                        <Icon className="w-10 h-10 text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                                    <p className="text-gray-600 text-lg leading-relaxed">{feature.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 md:py-20 relative px-4 sm:px-6" style={{ background: 'linear-gradient(to right, var(--color-primary), color-mix(in srgb, var(--color-primary), black 20%))' }}>
                <div className="container mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
                        Hala Karar Veremediniz Mi?
                    </h2>
                    <p className="text-white/90 text-xl mb-8 max-w-2xl mx-auto">
                        Uzman sigorta danışmanlarımız size en uygun çözümü bulmanız için yardımcı olsun.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        {/* DİNAMİK TELEFON NUMARASI BURADA KULLANILIYOR */}
                        <a href={`tel:${phone.replace(/\s+/g, '')}`} className="px-8 py-4 rounded-xl bg-white font-bold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center" style={{ color: 'var(--color-primary)' }}>
                            <Phone className="w-5 h-5 md:w-6 md:h-6 mr-2" />
                            {phone}
                        </a>
                        <button className="px-8 py-4 rounded-xl bg-transparent text-white font-bold text-lg border-2 border-white hover:bg-white transition-all duration-300 flex items-center justify-center hover:text-slate-900">
                            Canlı Destek
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}